import { LangKey } from "@vixi/localization";
import { capitalize } from "lodash";

export const drawingStyles = ["claymation", "disney", "ghibli", "anime", "puppet"] as const;

export interface PreferenceOption<
  k extends string | number | symbol = string,
  subK extends string | number | symbol = string
> {
  label: LangKey;
  value: string;
  images: string[];
  subPreferences?: PreferenceTree<k, subK>[];
}
export interface PreferenceTree<
  k extends string | number | symbol,
  subK extends string | number | symbol = string
> {
  title: LangKey;
  key: k;
  options: PreferenceOption<subK, k | subK>[];
  variants?: {
    form: Record<string, any>;
    override: Omit<PreferenceTree<k, subK>, "variants" | "options">;
  }[];
}

// Helpers
/**
 * Generates an array of drawing style options for the avatar preferences.
 * @param path - The path to the images folder (without the trailing slash)
 * @returns An array of objects representing the drawing style options
 */
export const drawingStyleOptions = <k extends string, subK extends string>(path: string) =>
  drawingStyles.map(
    (style) =>
      ({
        label: `Avatars.Style.${capitalize(style)}`,
        value: style,
        images: [`${path}/${style}.png`],
      } satisfies PreferenceOption<k, subK>)
  );

// Constants
export const preference_tree = {
  title: "Avatars.GenderTitle",
  key: "gender" as const,
  options: [
    {
      label: "Gender.Male",
      value: "male",
      images: [
        "/images/avatars/preferences/male/male-traditional.png",
        "/images/avatars/preferences/male/male-morgan.png",
      ],
      subPreferences: [
        {
          title: "Avatars.StyleTitle",
          key: "style" as const,
          options: drawingStyleOptions("/images/avatars/preferences/male/styles"),
        },
        {
          title: "Avatars.OutfitTitle",
          key: "outfit" as const,
          options: [
            {
              label: "Avatars.Outfit.Thawb",
              value: "a thawb (arabic traditional dress for men)",
              images: ["/images/avatars/preferences/male/outfits/thawb.png"],
            },
            {
              label: "Avatars.Outfit.Casual",
              value: "a casual shirt and a pair of jeans",
              images: ["/images/avatars/preferences/male/outfits/casual.png"],
            },
            {
              label: "Avatars.Outfit.Formal",
              value: "a formal suit",
              images: ["/images/avatars/preferences/male/outfits/formal.png"],
            },
          ],
        },
      ],
    },
    {
      label: "Gender.Female",
      value: "female",
      images: [
        "/images/avatars/preferences/female/female-hijabi.webp",
        "/images/avatars/preferences/female/female.webp",
      ],
      subPreferences: [
        {
          title: "Avatars.HijabiTitle",
          key: "hijabi" as const,
          options: [
            {
              label: "Avatars.Hijabi",
              images: ["/images/avatars/preferences/female/hijabi/index.png"],
              value: "hijabi",
              subPreferences: [
                {
                  title: "Avatars.StyleTitle",
                  key: "style" as const,
                  options: drawingStyleOptions("/images/avatars/preferences/female/hijabi/styles"),
                },
                {
                  title: "Avatars.OutfitTitle",
                  key: "outfit" as const,
                  options: [
                    {
                      label: "Avatars.Outfit.Abaya",
                      value: "a traditional abaya",
                      images: ["/images/avatars/preferences/female/hijabi/outfits/abaya.png"],
                    },
                    {
                      label: "Avatars.Outfit.Formal",
                      value: "a formal suit with a hijab",
                      images: ["/images/avatars/preferences/female/hijabi/outfits/formal.png"],
                    },
                    {
                      label: "Avatars.Outfit.Casual",
                      value: "a casual outfit with a hijab",
                      images: ["/images/avatars/preferences/female/hijabi/outfits/casual.png"],
                    },
                  ],
                },
              ],
            },
            {
              label: "Avatars.NonHijabi",
              images: ["/images/avatars/preferences/female/nonhijabi/index.webp"],
              value: "nonhijabi",
              subPreferences: [
                {
                  title: "Avatars.StyleTitle",
                  key: "style" as const,
                  options: drawingStyleOptions(
                    "/images/avatars/preferences/female/nonhijabi/styles"
                  ),
                },
                {
                  title: "Avatars.OutfitTitle",
                  key: "outfit" as const,
                  options: [
                    {
                      label: "Avatars.Outfit.Formal",
                      value: "a formal suit",
                      images: ["/images/avatars/preferences/female/nonhijabi/outfits/formal.png"],
                    },
                    {
                      label: "Avatars.Outfit.Casual",
                      value: "a casual outfit",
                      images: ["/images/avatars/preferences/female/nonhijabi/outfits/casual.png"],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
} satisfies PreferenceTree<string, string>;

export type PreferenceDict<
  T extends PreferenceTree<k, subk>,
  k extends string = T["key"],
  subk extends string = NonNullable<T["options"][number]["subPreferences"]>[number]["key"]
> = {
  [key in k | subk]: string;
};

export type PreferenceFormValues = PreferenceDict<typeof preference_tree>;
